import React, { useState, useEffect } from 'react';
import { useAuth } from '../auth';
import { getFollowers, getFollowing, getUserPhotos, getBlockedUsers, deleteAccount } from '../photoFunctions';
import { supabase } from '../supabase';
import PrivacyPolicy from './privacyPolicy';

export default function Settings({ onBack }) {
  const { user, profile, signOut } = useAuth();
  const [showPrivacyPolicy, setShowPrivacyPolicy] = useState(false);
  const [followers, setFollowers] = useState([]);
  const [following, setFollowing] = useState([]);
  const [photos, setPhotos] = useState([]);
  const [blockedUsers, setBlockedUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [deleting, setDeleting] = useState(false);

  useEffect(() => {
    if (user) {
      loadStats();
    }
  }, [user]);

  const loadStats = async () => {
    if (!user || !user.id) {
      console.error('Settings - No user or user.id available');
      return;
    }

    try {
      setLoading(true);
      const userId = user.id;
      
      // Use Promise.all to fetch all stats in parallel
      const [followersData, followingData, blockedUsersData] = await Promise.all([
        getFollowers(userId),
        getFollowing(userId),
        getBlockedUsers(userId)
      ]);
      
      // For photos, try direct query first to debug, then fall back to getUserPhotos
      let photosData = [];
      try {
        // Try direct query to see if it works
        const { data: directPhotos, error: directError } = await supabase
          .from('photos')
          .select('*')
          .eq('user_id', userId)
          .order('created_at', { ascending: false });
        
        if (directError) {
          console.error('Direct photos query error:', directError);
          // Fall back to getUserPhotos
          photosData = await getUserPhotos(userId, userId);
        } else {
          photosData = directPhotos || [];
          console.log('Direct query successful, got photos:', photosData.length);
        }
      } catch (photosError) {
        console.error('Error fetching photos:', photosError);
        // Try getUserPhotos as fallback
        try {
          photosData = await getUserPhotos(userId, userId);
        } catch (fallbackError) {
          console.error('getUserPhotos also failed:', fallbackError);
          photosData = [];
        }
      }
      
      // Debug logging
      console.log('Settings - Loaded stats:', {
        userId: userId,
        followersCount: followersData?.length || 0,
        followingCount: followingData?.length || 0,
        photosCount: photosData?.length || 0,
        photosSample: photosData?.slice(0, 2) // Log first 2 photos for debugging
      });
      
      setFollowers(followersData || []);
      setFollowing(followingData || []);
      setPhotos(photosData || []);
      setBlockedUsers(blockedUsersData || []);
    } catch (error) {
      console.error('Error loading stats:', error);
      console.error('Error details:', {
        message: error.message,
        stack: error.stack,
        userId: user?.id
      });
      // Set empty arrays on error to prevent undefined errors
      setFollowers([]);
      setFollowing([]);
      setPhotos([]);
      setBlockedUsers([]);
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteAccount = async () => {
    if (!user) return;
    
    setDeleting(true);
    try {
      // Delete all user data
      await deleteAccount(user.id);
      
      // Sign out the user using auth context (which clears localStorage properly)
      await signOut();
      
      // Reload the page to reset the app state
      window.location.href = '/';
    } catch (error) {
      console.error('Error deleting account:', error);
      alert('Failed to delete account. Please try again.');
      setDeleting(false);
    }
  };

  if (showPrivacyPolicy) {
    return <PrivacyPolicy onBack={() => setShowPrivacyPolicy(false)} />;
  }

  return (
    <div style={{
      fontFamily: "'Montserrat', sans-serif",
      background: '#0a1628',
      minHeight: '100vh',
      color: '#fff',
      position: 'relative',
      paddingTop: 'env(safe-area-inset-top)',
      paddingBottom: 'env(safe-area-inset-bottom)',
      paddingLeft: 'env(safe-area-inset-left)',
      paddingRight: 'env(safe-area-inset-right)'
    }}>
      {/* Back Button */}
      <button
        onClick={onBack}
        style={{
          position: 'fixed',
          top: 'calc(40px + env(safe-area-inset-top))',
          left: 'calc(40px + env(safe-area-inset-left))',
          background: 'none',
          border: '1px solid rgba(198,162,91,0.4)',
          color: '#c6a25b',
          fontFamily: "'Montserrat', sans-serif",
          fontSize: '1rem',
          letterSpacing: '0.1em',
          textTransform: 'uppercase',
          padding: '12px 24px',
          cursor: 'pointer',
          transition: 'all 0.4s ease-in-out',
          backdropFilter: 'blur(5px)',
          minHeight: '44px',
          zIndex: 100,
          WebkitTapHighlightColor: 'transparent'
        }}
        onMouseOver={(e) => {
          e.currentTarget.style.background = 'rgba(198,162,91,0.15)';
          e.currentTarget.style.borderColor = '#c6a25b';
          e.currentTarget.style.color = '#e6c57e';
        }}
        onMouseOut={(e) => {
          e.currentTarget.style.background = 'none';
          e.currentTarget.style.borderColor = 'rgba(198,162,91,0.4)';
          e.currentTarget.style.color = '#c6a25b';
        }}
      >
        ← Back
      </button>

      {/* Content */}
      <div style={{
        maxWidth: '800px',
        margin: '0 auto',
        padding: '120px 40px 80px',
        boxSizing: 'border-box'
      }}>
        <h1 style={{
          fontFamily: "'Bodoni Moda', serif",
          fontSize: 'clamp(2.5rem, 5vw, 4rem)',
          color: '#e6c57e',
          letterSpacing: '0.05em',
          fontWeight: 400,
          marginBottom: '40px',
          textShadow: '2px 2px 10px rgba(49,35,16,0.5)'
        }}>
          Settings
        </h1>

        {/* Account Stats */}
        <section style={{ marginBottom: '40px' }}>
          <h2 style={{
            fontFamily: "'Bodoni Moda', serif",
            fontSize: '1.5rem',
            fontWeight: 400,
            letterSpacing: '0.08em',
            textTransform: 'uppercase',
            color: '#e6c57e',
            marginBottom: '20px'
          }}>
            Account Statistics
          </h2>

          {loading ? (
            <p style={{ color: '#b4bad0', fontSize: '0.95rem' }}>Loading...</p>
          ) : (
            <div style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
              gap: '20px',
              marginBottom: '30px'
            }}>
              <div style={{
                background: 'rgba(198,162,91,0.1)',
                border: '1px solid rgba(198,162,91,0.3)',
                borderRadius: '8px',
                padding: '20px',
                textAlign: 'center'
              }}>
                <div style={{
                  fontSize: '2.5rem',
                  fontWeight: 600,
                  color: '#e6c57e',
                  marginBottom: '8px'
                }}>
                  {photos.length}
                </div>
                <div style={{
                  fontSize: '0.9rem',
                  color: '#b4bad0',
                  textTransform: 'uppercase',
                  letterSpacing: '0.05em'
                }}>
                  Photos
                </div>
              </div>

              <div style={{
                background: 'rgba(198,162,91,0.1)',
                border: '1px solid rgba(198,162,91,0.3)',
                borderRadius: '8px',
                padding: '20px',
                textAlign: 'center'
              }}>
                <div style={{
                  fontSize: '2.5rem',
                  fontWeight: 600,
                  color: '#e6c57e',
                  marginBottom: '8px'
                }}>
                  {followers.length}
                </div>
                <div style={{
                  fontSize: '0.9rem',
                  color: '#b4bad0',
                  textTransform: 'uppercase',
                  letterSpacing: '0.05em'
                }}>
                  Followers
                </div>
              </div>

              <div style={{
                background: 'rgba(198,162,91,0.1)',
                border: '1px solid rgba(198,162,91,0.3)',
                borderRadius: '8px',
                padding: '20px',
                textAlign: 'center'
              }}>
                <div style={{
                  fontSize: '2.5rem',
                  fontWeight: 600,
                  color: '#e6c57e',
                  marginBottom: '8px'
                }}>
                  {following.length}
                </div>
                <div style={{
                  fontSize: '0.9rem',
                  color: '#b4bad0',
                  textTransform: 'uppercase',
                  letterSpacing: '0.05em'
                }}>
                  Following
                </div>
              </div>
            </div>
          )}

          {/* Followers List */}
          {!loading && followers.length > 0 && (
            <div style={{ marginBottom: '30px' }}>
              <h3 style={{
                fontFamily: "'Montserrat', sans-serif",
                fontSize: '1.1rem',
                fontWeight: 500,
                color: '#e6c57e',
                marginBottom: '15px'
              }}>
                Your Followers ({followers.length})
              </h3>
              <div style={{
                background: 'rgba(198,162,91,0.05)',
                border: '1px solid rgba(198,162,91,0.2)',
                borderRadius: '8px',
                padding: '20px',
                maxHeight: '300px',
                overflowY: 'auto'
              }}>
                {followers.map((follower) => (
                  <div
                    key={follower.id}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '12px',
                      padding: '12px 0',
                      borderBottom: followers.indexOf(follower) < followers.length - 1 ? '1px solid rgba(198,162,91,0.1)' : 'none'
                    }}
                  >
                    <div style={{
                      width: '40px',
                      height: '40px',
                      borderRadius: '50%',
                      background: follower.avatar
                        ? `url(${follower.avatar}) center/cover`
                        : '#c6a25b',
                      border: '2px solid rgba(198,162,91,0.4)',
                      flexShrink: 0,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontSize: '1.2em',
                      color: '#312310',
                      fontFamily: "'Apple Garamond', Garamond, 'Times New Roman', serif",
                      fontWeight: 400
                    }}>
                      {!follower.avatar && (follower.username?.[0] || 'U').toUpperCase()}
                    </div>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{
                        fontFamily: "'Montserrat', sans-serif",
                        fontSize: '0.95rem',
                        fontWeight: 500,
                        color: '#d4d4d0',
                        marginBottom: '2px',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap'
                      }}>
                        {follower.username || 'Unknown'}
                      </div>
                      {follower.bio && (
                        <div style={{
                          fontFamily: "'Montserrat', sans-serif",
                          fontSize: '0.85rem',
                          color: '#b4bad0',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap'
                        }}>
                          {follower.bio}
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Following List */}
          {!loading && following.length > 0 && (
            <div>
              <h3 style={{
                fontFamily: "'Montserrat', sans-serif",
                fontSize: '1.1rem',
                fontWeight: 500,
                color: '#e6c57e',
                marginBottom: '15px'
              }}>
                You're Following ({following.length})
              </h3>
              <div style={{
                background: 'rgba(198,162,91,0.05)',
                border: '1px solid rgba(198,162,91,0.2)',
                borderRadius: '8px',
                padding: '20px',
                maxHeight: '300px',
                overflowY: 'auto'
              }}>
                {following.map((user) => (
                  <div
                    key={user.id}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '12px',
                      padding: '12px 0',
                      borderBottom: following.indexOf(user) < following.length - 1 ? '1px solid rgba(198,162,91,0.1)' : 'none'
                    }}
                  >
                    <div style={{
                      width: '40px',
                      height: '40px',
                      borderRadius: '50%',
                      background: user.avatar
                        ? `url(${user.avatar}) center/cover`
                        : '#c6a25b',
                      border: '2px solid rgba(198,162,91,0.4)',
                      flexShrink: 0,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontSize: '1.2em',
                      color: '#312310',
                      fontFamily: "'Apple Garamond', Garamond, 'Times New Roman', serif",
                      fontWeight: 400
                    }}>
                      {!user.avatar && (user.username?.[0] || 'U').toUpperCase()}
                    </div>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{
                        fontFamily: "'Montserrat', sans-serif",
                        fontSize: '0.95rem',
                        fontWeight: 500,
                        color: '#d4d4d0',
                        marginBottom: '2px',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap'
                      }}>
                        {user.username || 'Unknown'}
                      </div>
                      {user.bio && (
                        <div style={{
                          fontFamily: "'Montserrat', sans-serif",
                          fontSize: '0.85rem',
                          color: '#b4bad0',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap'
                        }}>
                          {user.bio}
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Blocked Users List */}
          {!loading && blockedUsers.length > 0 && (
            <div style={{ marginTop: '30px' }}>
              <h3 style={{
                fontFamily: "'Montserrat', sans-serif",
                fontSize: '1.1rem',
                fontWeight: 500,
                color: '#e6c57e',
                marginBottom: '15px'
              }}>
                Blocked Users ({blockedUsers.length})
              </h3>
              <div style={{
                background: 'rgba(198,162,91,0.05)',
                border: '1px solid rgba(198,162,91,0.2)',
                borderRadius: '8px',
                padding: '20px',
                maxHeight: '300px',
                overflowY: 'auto'
              }}>
                {blockedUsers.map((blockedUser) => (
                  <div
                    key={blockedUser.id}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '12px',
                      padding: '12px 0',
                      borderBottom: blockedUsers.indexOf(blockedUser) < blockedUsers.length - 1 ? '1px solid rgba(198,162,91,0.1)' : 'none'
                    }}
                  >
                    <div style={{
                      width: '40px',
                      height: '40px',
                      borderRadius: '50%',
                      background: blockedUser.avatar
                        ? `url(${blockedUser.avatar}) center/cover`
                        : '#c6a25b',
                      border: '2px solid rgba(198,162,91,0.4)',
                      flexShrink: 0,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontSize: '1.2em',
                      color: '#312310',
                      fontFamily: "'Apple Garamond', Garamond, 'Times New Roman', serif",
                      fontWeight: 400
                    }}>
                      {!blockedUser.avatar && (blockedUser.username?.[0] || 'U').toUpperCase()}
                    </div>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{
                        fontFamily: "'Montserrat', sans-serif",
                        fontSize: '0.95rem',
                        fontWeight: 500,
                        color: '#d4d4d0',
                        marginBottom: '2px',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap'
                      }}>
                        {blockedUser.username || 'Unknown'}
                      </div>
                      {blockedUser.name && (
                        <div style={{
                          fontFamily: "'Montserrat', sans-serif",
                          fontSize: '0.85rem',
                          color: '#b4bad0',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap'
                        }}>
                          {blockedUser.name}
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {!loading && blockedUsers.length === 0 && (
            <div style={{ marginTop: '30px' }}>
              <p style={{
                fontFamily: "'Montserrat', sans-serif",
                fontSize: '0.95rem',
                color: '#b4bad0',
                fontStyle: 'italic'
              }}>
                No blocked users
              </p>
            </div>
          )}
        </section>

        {/* Privacy Policy Link */}
        <section style={{ marginBottom: '40px' }}>
          <h2 style={{
            fontFamily: "'Bodoni Moda', serif",
            fontSize: '1.5rem',
            fontWeight: 400,
            letterSpacing: '0.08em',
            textTransform: 'uppercase',
            color: '#e6c57e',
            marginBottom: '20px'
          }}>
            Privacy & Legal
          </h2>
          <button
            onClick={() => setShowPrivacyPolicy(true)}
            style={{
              background: 'rgba(198,162,91,0.1)',
              border: '1px solid rgba(198,162,91,0.3)',
              color: '#e6c57e',
              fontFamily: "'Montserrat', sans-serif",
              fontSize: '1rem',
              letterSpacing: '0.05em',
              padding: '16px 24px',
              cursor: 'pointer',
              transition: 'all 0.3s ease',
              borderRadius: '8px',
              width: '100%',
              textAlign: 'left',
              minHeight: '44px'
            }}
            onMouseOver={(e) => {
              e.currentTarget.style.background = 'rgba(198,162,91,0.2)';
              e.currentTarget.style.borderColor = '#c6a25b';
            }}
            onMouseOut={(e) => {
              e.currentTarget.style.background = 'rgba(198,162,91,0.1)';
              e.currentTarget.style.borderColor = 'rgba(198,162,91,0.3)';
            }}
          >
            View Privacy Policy →
          </button>
        </section>

        {/* Delete Account Section */}
        <section>
          <h2 style={{
            fontFamily: "'Bodoni Moda', serif",
            fontSize: '1.5rem',
            fontWeight: 400,
            letterSpacing: '0.08em',
            textTransform: 'uppercase',
            color: '#e6c57e',
            marginBottom: '20px'
          }}>
            Account Management
          </h2>
          <button
            onClick={() => setShowDeleteConfirm(true)}
            style={{
              background: 'rgba(175,115,31,0.2)',
              border: '1px solid rgba(130,85,30,0.3)',
              color: '#82551e',
              fontFamily: "'Montserrat', sans-serif",
              fontSize: '1rem',
              letterSpacing: '0.1em',
              textTransform: 'uppercase',
              padding: '16px 24px',
              cursor: 'pointer',
              transition: 'all 0.3s ease',
              borderRadius: '8px',
              width: '100%',
              textAlign: 'left',
              minHeight: '44px',
              fontWeight: 400
            }}
            onMouseOver={(e) => {
              e.currentTarget.style.background = 'rgba(175,115,31,0.4)';
              e.currentTarget.style.borderColor = '#82551e';
            }}
            onMouseOut={(e) => {
              e.currentTarget.style.background = 'rgba(175,115,31,0.2)';
              e.currentTarget.style.borderColor = 'rgba(130,85,30,0.3)';
            }}
          >
            Delete My Account
          </button>
        </section>
      </div>

      {/* Delete Confirmation Modal */}
      {showDeleteConfirm && (
        <>
          <div 
            style={{
              position: 'fixed',
              top: 0,
              left: 0,
              width: '100%',
              height: '100%',
              background: 'rgba(49,35,16,0.7)',
              backdropFilter: 'blur(8px)',
              zIndex: 6000,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              padding: '20px'
            }}
            onClick={() => !deleting && setShowDeleteConfirm(false)}
          />
          <div 
            style={{
              position: 'fixed',
              top: '50%',
              left: '50%',
              transform: 'translate(-50%, -50%)',
              width: 'calc(100vw - 40px)',
              maxWidth: '400px',
              background: 'rgba(174,180,184,0.95)',
              backdropFilter: 'blur(20px)',
              border: '1px solid rgba(130,85,30,0.2)',
              boxShadow: '0 8px 30px rgba(49,35,16,0.3)',
              borderRadius: '8px',
              padding: 'clamp(20px, 5vw, 32px)',
              zIndex: 6001,
              animation: 'slideDown 0.3s ease'
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <h3 style={{
              fontFamily: "'Apple Garamond', Garamond, 'Times New Roman', serif",
              fontSize: 'clamp(1.2em, 4vw, 1.6em)',
              color: '#312310',
              letterSpacing: '0.05em',
              fontWeight: 400,
              margin: '0 0 16px 0'
            }}>
              Delete Account
            </h3>
            <p style={{
              fontFamily: "'Montserrat', sans-serif",
              fontSize: 'clamp(0.8em, 2.5vw, 0.9em)',
              color: '#553918',
              letterSpacing: '0.02em',
              lineHeight: 1.6,
              fontWeight: 300,
              margin: '0 0 24px 0'
            }}>
              This will permanently delete your account and all associated data:
            </p>
            <ul style={{
              fontFamily: "'Montserrat', sans-serif",
              fontSize: 'clamp(0.75em, 2.2vw, 0.85em)',
              color: '#553918',
              letterSpacing: '0.02em',
              lineHeight: 1.8,
              fontWeight: 300,
              margin: '0 0 24px 0',
              paddingLeft: '20px'
            }}>
              <li>All your images and photos</li>
              <li>Your profile and bio</li>
              <li>All followers and following relationships</li>
              <li>All blocked user relationships</li>
            </ul>
            <p style={{
              fontFamily: "'Montserrat', sans-serif",
              fontSize: 'clamp(0.75em, 2.2vw, 0.85em)',
              color: '#82551e',
              letterSpacing: '0.02em',
              lineHeight: 1.6,
              fontWeight: 400,
              margin: '0 0 24px 0',
              fontStyle: 'italic'
            }}>
              This action cannot be undone.
            </p>
            <div style={{
              display: 'flex',
              gap: '12px',
              justifyContent: 'flex-end'
            }}>
              <button
                onClick={() => setShowDeleteConfirm(false)}
                disabled={deleting}
                style={{
                  padding: '10px 20px',
                  background: 'rgba(162,169,172,0.5)',
                  border: '1px solid rgba(130,85,30,0.3)',
                  color: '#312310',
                  fontFamily: "'Montserrat', sans-serif",
                  fontSize: 'clamp(0.7em, 2vw, 0.8em)',
                  letterSpacing: '0.1em',
                  textTransform: 'uppercase',
                  fontWeight: 400,
                  cursor: deleting ? 'not-allowed' : 'pointer',
                  borderRadius: '4px',
                  transition: 'all 0.3s ease',
                  minHeight: '44px',
                  minWidth: '100px',
                  WebkitTapHighlightColor: 'transparent',
                  opacity: deleting ? 0.6 : 1
                }}
                onMouseOver={(e) => {
                  if (!deleting) {
                    e.target.style.background = 'rgba(162,169,172,0.7)';
                  }
                }}
                onMouseOut={(e) => {
                  e.target.style.background = 'rgba(162,169,172,0.5)';
                }}
              >
                Go Back
              </button>
              <button
                onClick={handleDeleteAccount}
                disabled={deleting}
                style={{
                  padding: '10px 20px',
                  background: deleting ? 'rgba(175,115,31,0.4)' : 'rgba(175,115,31,0.6)',
                  border: '1px solid rgba(130,85,30,0.4)',
                  color: '#312310',
                  fontFamily: "'Montserrat', sans-serif",
                  fontSize: 'clamp(0.7em, 2vw, 0.8em)',
                  letterSpacing: '0.1em',
                  textTransform: 'uppercase',
                  fontWeight: 400,
                  cursor: deleting ? 'not-allowed' : 'pointer',
                  borderRadius: '4px',
                  transition: 'all 0.3s ease',
                  minHeight: '44px',
                  minWidth: '120px',
                  WebkitTapHighlightColor: 'transparent'
                }}
                onMouseOver={(e) => {
                  if (!deleting) {
                    e.target.style.background = 'rgba(175,115,31,0.8)';
                  }
                }}
                onMouseOut={(e) => {
                  e.target.style.background = deleting ? 'rgba(175,115,31,0.4)' : 'rgba(175,115,31,0.6)';
                }}
              >
                {deleting ? 'Deleting...' : 'Confirm Delete'}
              </button>
            </div>
          </div>
        </>
      )}

      <style>{`
        @keyframes slideDown {
          from {
            opacity: 0;
            transform: translateY(-20px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
        div::-webkit-scrollbar {
          width: 6px;
        }
        div::-webkit-scrollbar-track {
          background: rgba(130,85,30,0.1);
          border-radius: 3px;
        }
        div::-webkit-scrollbar-thumb {
          background: rgba(175,115,31,0.4);
          border-radius: 3px;
        }
        div::-webkit-scrollbar-thumb:hover {
          background: rgba(175,115,31,0.6);
        }
      `}</style>
    </div>
  );
}

